import { type InstancePerson, parsePersonAttributeValue } from "@keydials/api-client";

/**
 * A Person attribute (KEY-PEOPLE) names one or more entries in the dial's own people list — people the dial
 * keeps track of who need not hold an account. Everything the field needs is on the instance detail
 * (`people` comes with every fetch), so resolving a value costs no extra call and the picker offers exactly
 * the people the server will accept. The shape mirrors `userAttribute.ts`, and so does the one rule that
 * matters: a value naming someone since removed from the list is kept and flagged, never blanked.
 */

/** What to call a person: their name on the dial, with their account's name after it when linked and different. */
export function personLabel(person: InstancePerson): string {
  const account = person.userDisplayName?.trim();
  return account && account.toLowerCase() !== person.name.trim().toLowerCase()
    ? `${person.name} (${account})`
    : person.name;
}

export interface ResolvedPerson {
  id: string;
  /** The list entry, or null when the id names nobody on the list any more. */
  person: InstancePerson | null;
}

export interface PersonAttributeState {
  /** The ids the value names, in the order they were picked; empty when unset. */
  ids: string[];
  /** Each id resolved against the list, in the same order. */
  resolved: ResolvedPerson[];
  /** At least one id names nobody on the list: kept as it was and flagged, like a revoked User value. */
  unresolved: boolean;
  /** The people the picker offers, in the dial's own order. */
  options: InstancePerson[];
}

/** Resolves what a Person field should show and offer, against the people already on the instance detail. */
export function personAttributeState(value: string, people: InstancePerson[] | null | undefined): PersonAttributeState {
  const list = people ?? [];
  const ids = parsePersonAttributeValue(value);
  const resolved = ids.map((id) => ({
    id,
    // Ids arrive canonicalised, but a value could predate that or arrive by import: compare case-insensitively.
    person: list.find((p) => p.id.toLowerCase() === id.toLowerCase()) ?? null,
  }));
  return {
    ids,
    resolved,
    unresolved: resolved.some((r) => r.person === null),
    options: [...list].sort((a, b) => a.order - b.order),
  };
}
